import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useCurrency } from '@/contexts/CurrencyContext'
import { toastHelpers } from '@/lib/toast-helpers'
import { clockTime } from '@/lib/onlineOrderTiming'
import { CUSTODY_LABEL } from '@/lib/riderLinks'
import type { DispatchRider, RiderCash } from '@/types/rider'

// Rider hand-in (F-DLV-N1): count what the rider brings back against what
// they collected. Any gap needs a note and stays on the (append-only) hand-in
// record; Day Close will not lock while a rider still holds the day's money.

function paisa(n: number): number {
  return Math.round(n * 100)
}

export function HandinDialog({ rider, onClose }: { rider: DispatchRider | null; onClose: () => void }) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const [cash, setCash] = useState('')
  const [wallet, setWallet] = useState('')
  const [note, setNote] = useState('')

  const { data } = useQuery({
    queryKey: ['dispatch', 'rider-cash', rider?.id],
    queryFn: () => apiClient.getDispatchRiderCash(rider!.id),
    enabled: !!rider,
  })
  const held = data?.data as RiderCash | undefined

  useEffect(() => {
    if (held) {
      setCash(String(held.held.cash))
      setWallet(String(held.held.wallet))
      setNote('')
    }
  }, [held])

  const counted = Number(cash) || 0
  const confirmed = Number(wallet) || 0
  const gap = held ? (paisa(held.held.cash) - paisa(counted) + paisa(held.held.wallet) - paisa(confirmed)) / 100 : 0
  const canSubmit = !!held && held.held.count > 0 && (gap === 0 || note.trim() !== '')

  const handin = useMutation({
    mutationFn: () => apiClient.riderHandin(rider!.id, { counted_cash: counted, confirmed_wallet: confirmed, note }),
    onSuccess: (res) => {
      const short = res.data?.shortage ?? 0
      toastHelpers.success(
        'Hand-in recorded',
        short > 0 ? `${rider?.name} is short ${formatCurrency(short)}` : short < 0 ? `Over by ${formatCurrency(-short)}` : undefined,
      )
      qc.invalidateQueries({ queryKey: ['dispatch'] })
      onClose()
    },
    onError: (err: unknown) =>
      toastHelpers.error('Could not record hand-in', (err as { message?: string })?.message || 'Please try again.'),
  })

  return (
    <Dialog open={!!rider} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Hand-in · {rider?.name}</DialogTitle>
          <DialogDescription>Count the cash and check the wallet transfers before confirming.</DialogDescription>
        </DialogHeader>

        {!held ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="space-y-4">
            <ul className="max-h-56 divide-y overflow-y-auto rounded-lg border text-sm">
              {held.collections.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-2 px-3 py-1.5">
                  <span>
                    #{c.order_number}
                    <span className="block text-xs text-muted-foreground">
                      {CUSTODY_LABEL[c.custody]}
                      {c.payment_sub_method ? ` · ${c.payment_sub_method}` : ''}
                      {c.reference_number ? ` · ${c.reference_number}` : ''} · {clockTime(c.collected_at)}
                    </span>
                  </span>
                  <span>{formatCurrency(c.amount)}</span>
                </li>
              ))}
            </ul>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="handin-cash">Cash counted</Label>
                <Input id="handin-cash" inputMode="decimal" value={cash} onChange={(e) => setCash(e.target.value.replace(/[^\d.]/g, ''))} />
                <p className="text-xs text-muted-foreground">Expected {formatCurrency(held.held.cash)}</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="handin-wallet">Wallet received</Label>
                <Input id="handin-wallet" inputMode="decimal" value={wallet} onChange={(e) => setWallet(e.target.value.replace(/[^\d.]/g, ''))} />
                <p className="text-xs text-muted-foreground">Expected {formatCurrency(held.held.wallet)}</p>
              </div>
            </div>

            {gap !== 0 && (
              <p className={`text-sm font-medium ${gap > 0 ? 'text-red-600' : 'text-amber-700 dark:text-amber-400'}`}>
                {gap > 0 ? `Short by ${formatCurrency(gap)}` : `Over by ${formatCurrency(-gap)}`} — add a note.
              </p>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="handin-note">Note {gap !== 0 ? '' : '(optional)'}</Label>
              <Textarea id="handin-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => handin.mutate()} disabled={!canSubmit || handin.isPending}>
            Confirm hand-in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
